import React from "react";
import type { Geometry } from "geojson";
import { LayerType } from "src/common/managers/Types";
import type { ANNAnnotationRecord } from "./ANNTypes";
import { AnnotationContextType } from "./ANNTypes";
export interface AnnotationLayerCandidate {
   layerId: number;
   layerName: string;
   datasetName?: string;
   layerType: LayerType;
   dataUrl?: string;
   coordinates?: [number, number][];
   locked?: boolean;
   geoJsonGeometries?: Geometry[];
}
export interface ANNState {
   isInitialized: boolean;
   currentContextType?: AnnotationContextType;
   currentIdentifier?: string | number;
   currentOrganizationId?: string;
   annotations: ANNAnnotationRecord[];
   loading: boolean;
   saving: boolean;
   error?: string;
   annLayers: AnnotationLayerCandidate[];
}
export const initialANNState = (): ANNState => ({
   isInitialized: false,
   currentContextType: undefined,
   currentIdentifier: undefined,
   currentOrganizationId: undefined,
   annotations: [],
   loading: false,
   saving: false,
   error: undefined,
   annLayers: [],
});
export enum ANNActionType {
   LoadAnnotationsRequest = "LoadAnnotationsRequest",
   LoadAnnotationsSuccess = "LoadAnnotationsSuccess",
   LoadAnnotationsFailure = "LoadAnnotationsFailure",
   SaveAnnotationRequest = "SaveAnnotationRequest",
   SaveAnnotationSuccess = "SaveAnnotationSuccess",
   SaveAnnotationFailure = "SaveAnnotationFailure",
   SetAnnLayers = "SetAnnLayers",
}
export interface LoadAnnotationsRequestAction {
   type: ANNActionType.LoadAnnotationsRequest;
   payload: { contextType?: AnnotationContextType; identifier?: string | number; organizationId?: string };
}
export interface LoadAnnotationsSuccessAction {
   type: ANNActionType.LoadAnnotationsSuccess;
   payload: { contextType?: AnnotationContextType; identifier?: string | number; organizationId?: string; annotations: ANNAnnotationRecord[] };
}
export interface LoadAnnotationsFailureAction {
   type: ANNActionType.LoadAnnotationsFailure;
   payload: { contextType?: AnnotationContextType; identifier?: string | number; organizationId?: string; error: string };
}
export interface SaveAnnotationRequestAction {
   type: ANNActionType.SaveAnnotationRequest;
}
export interface SaveAnnotationSuccessAction {
   type: ANNActionType.SaveAnnotationSuccess;
   payload: ANNAnnotationRecord;
}
export interface SaveAnnotationFailureAction {
   type: ANNActionType.SaveAnnotationFailure;
   payload: string;
}
export interface SetAnnLayersAction {
   type: ANNActionType.SetAnnLayers;
   payload: AnnotationLayerCandidate[];
}
export type ANNAction =
   | LoadAnnotationsRequestAction
   | LoadAnnotationsSuccessAction
   | LoadAnnotationsFailureAction
   | SaveAnnotationRequestAction
   | SaveAnnotationSuccessAction
   | SaveAnnotationFailureAction
   | SetAnnLayersAction;
export const annLoadAnnotationsRequest = (
   contextType?: AnnotationContextType,
   identifier?: string | number,
   organizationId?: string
): LoadAnnotationsRequestAction => ({
   type: ANNActionType.LoadAnnotationsRequest,
   payload: { contextType, identifier, organizationId },
});
export const annLoadAnnotationsSuccess = (
   contextType: AnnotationContextType | undefined,
   identifier: string | number | undefined,
   organizationId: string | undefined,
   annotations: ANNAnnotationRecord[]
): LoadAnnotationsSuccessAction => ({
   type: ANNActionType.LoadAnnotationsSuccess,
   payload: { contextType, identifier, organizationId, annotations },
});
export const annLoadAnnotationsFailure = (
   contextType: AnnotationContextType | undefined,
   identifier: string | number | undefined,
   organizationId: string | undefined,
   error: string
): LoadAnnotationsFailureAction => ({
   type: ANNActionType.LoadAnnotationsFailure,
   payload: { contextType, identifier, organizationId, error },
});
export const annSaveAnnotationRequest = (): SaveAnnotationRequestAction => ({
   type: ANNActionType.SaveAnnotationRequest,
});
export const annSaveAnnotationSuccess = (annotation: ANNAnnotationRecord): SaveAnnotationSuccessAction => ({
   type: ANNActionType.SaveAnnotationSuccess,
   payload: annotation,
});
export const annSaveAnnotationFailure = (error: string): SaveAnnotationFailureAction => ({
   type: ANNActionType.SaveAnnotationFailure,
   payload: error,
});
export const annSetAnnLayers = (layers: AnnotationLayerCandidate[]): SetAnnLayersAction => ({
   type: ANNActionType.SetAnnLayers,
   payload: layers,
});
export const annReducer: React.Reducer<ANNState, ANNAction> = (state, action) => {
   switch (action.type) {
      case ANNActionType.LoadAnnotationsRequest:
         return {
            ...state,
            currentContextType: action.payload.contextType,
            currentIdentifier: action.payload.identifier,
            currentOrganizationId: action.payload.organizationId,
            loading: true,
            error: undefined,
         };
      case ANNActionType.LoadAnnotationsSuccess: {
         const { contextType, identifier, organizationId, annotations } = action.payload;
         if (
            (state.currentContextType && contextType && state.currentContextType !== contextType) ||
            (state.currentIdentifier && identifier && state.currentIdentifier !== identifier) ||
            (state.currentOrganizationId && organizationId && state.currentOrganizationId !== organizationId)
         ) {
            return state;
         }
         return {
            ...state,
            currentContextType: contextType,
            currentIdentifier: identifier,
            currentOrganizationId: organizationId,
            annotations,
            loading: false,
            isInitialized: true,
            error: undefined,
         };
      }
      case ANNActionType.LoadAnnotationsFailure: {
         const { contextType, identifier, organizationId, error } = action.payload;
         if (
            (state.currentContextType && contextType && state.currentContextType !== contextType) ||
            (state.currentIdentifier && identifier && state.currentIdentifier !== identifier) ||
            (state.currentOrganizationId && organizationId && state.currentOrganizationId !== organizationId)
         ) {
            return state;
         }
         return {
            ...state,
            loading: false,
            error,
         };
      }
      case ANNActionType.SaveAnnotationRequest:
         return { ...state, saving: true, error: undefined };
      case ANNActionType.SaveAnnotationSuccess: {
         const annotation = action.payload;
         const matchesContext = !state.currentContextType || state.currentContextType === annotation.contextType;
         const matchesIdentifier =
            !state.currentIdentifier ||
            (annotation.contextType === AnnotationContextType.Servitut && state.currentIdentifier === annotation.documentId) ||
            (annotation.contextType === AnnotationContextType.Ejendom && String(state.currentIdentifier) === String(annotation.bfeNumber));
         const matchesOrganization =
            !state.currentOrganizationId || state.currentOrganizationId === annotation.organizationId;
         const shouldAppend = matchesContext && matchesIdentifier && matchesOrganization;
         return {
            ...state,
            saving: false,
            annotations: shouldAppend ? [annotation, ...state.annotations] : state.annotations,
         };
      }
      case ANNActionType.SaveAnnotationFailure:
         return { ...state, saving: false, error: action.payload };
      case ANNActionType.SetAnnLayers:
         return { ...state, annLayers: action.payload };
      default:
         return state;
   }
};
export type ANNDispatch = React.Dispatch<ANNAction>;
export const ANNContext = React.createContext<{
   state: ANNState;
   dispatch: ANNDispatch;
}>({
   state: initialANNState(),
   dispatch: () => undefined,
});
export function transactionalANNReducer(action: ANNAction, dispatch: ANNDispatch) {
   dispatch(action);
}